import { Card, CardContent, Typography, Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { each, groupBy } from 'lodash'
import moment from 'moment'
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import PromoAndDiscountEntryDialog from '../Dialogs/Promos/PromoAndDiscountEntryDialog'
import DeletePromoConfirmation from '../Dialogs/Promos/DeletePromoConfirmation'
import InvoiceTable from '../../components/Tables/InvoiceTable'
import {
	filterAddOnsByTimeBlock,
	STATUS_ID,
	GROOMING,
	DAYCARE,
	BOARDING,
	COMPATIBILITY_SESSION,
	LINE_ITEM_TYPES,
	calculatePromoAmountToApply,
	getEffectiveAppliedPromo,
	getPromoApplicationFlags,
	getVisitPromoServiceTargets,
	resolveFacilityVisitId,
	resolveHouseholdId,
	VIP_MEMBER_PROMO_CODE,
	PROMO_SERVICE_TARGETS,
	MEMBERSHIP_TYPES_ENUM,
	VIP_MEMBERSHIP_TYPES,
	GROOMING_SERVICE_LABELS,
	getApplicableAmountToApplyToLineItem,
	MEAL,
	ADDON_ONLY,
	isSignatureStayPackageAddon
} from '../../utils'
import { deleteLineItem, updateVisitLineItem } from '../../actions/lineItems/lineItems.actions'
import usePromotions from '../../hooks/usePromotions'
import { createItem } from '../../actions/misc/misc.actions'
import * as Constants from '../../utils/constants'
import useGroomingVisit from '../../hooks/useGroomingVisit'
import { matchesFacility } from '../../utils/facilityMatch'

import useSelectedHousehold from '../../hooks/useSelectedHousehold'
import StyledAlert from '../Common/StyledAlert'

const useStyles = makeStyles((theme) => ({
	totalWrapper: {
		display: 'flex',
		justifyContent: 'flex-end'
	},
	promoWrapper: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center'
	},
	titleSection: {
		width: '150px',
		'& h6': {
			fontSize: '16px',
			lineHeight: '22px'
		}
	},
	totalSection: {
		width: '75px',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-end'
	}
}))

const VisitInvoiceCard = (props) => {
	const { visit, setIsEditItemPopupShown, setSelectedItem, addOnBank, facility } = props

	const dispatch = useDispatch()
	const [invoice, setInvoice] = useState([])
	const [total, setTotal] = useState(0)
	const [totalNumber, setTotalNumber] = useState(0)
	const [deletePromoShown, setDeletePromoShown] = useState(false)
	const [amtDue, setAmtDue] = useState(0)
	const [promoModal, setPromoModal] = useState(false)
	const [showBankCreditDisclaimer, setShowBankCreditDisclaimer] = useState(false)
	const { household, totalCreditAvailable, isVipOrPetCareOnDate } = useSelectedHousehold()
	const { groomingCreditTotal, groomingRates } = useGroomingVisit()

	const { deletePromoForFacilityVisit, validatePromoForFacilityVisit } = usePromotions()
	const [vipResolvedPromo, setVipResolvedPromo] = useState(null)

	const effectiveAppliedPromo = useMemo(() => getEffectiveAppliedPromo(visit), [visit])
	const promoForInvoice = effectiveAppliedPromo || vipResolvedPromo

	const visitStartDate = visit?.startDate || visit?.visit_date || visit?.dates?.[0]
	const membershipOnVisitStartDate = visitStartDate
		? isVipOrPetCareOnDate(visitStartDate, facility?._id)
		: { vip: false, petcare: false }
	const isVipOnVisitStartDate = membershipOnVisitStartDate.vip
	const hasFreeEarlyDropoff = membershipOnVisitStartDate.vip || membershipOnVisitStartDate.petcare

	const getEarlyDropoffDisplayPrice = (lineItemAmount) => {
		if (hasFreeEarlyDropoff) return 0
		return (
			lineItemAmount ??
			facility?.services?.daycare?.early_dropoff_fee ??
			0
		)
	}

	const getLatePickupDisplayPrice = () => 0

	const isExtendedVisit = visit.extended_pickup && visit.lineItems && visit.lineItems.length
	const isEarlyDropOff =
		facility?.early_dropoff_enabled && visit.early_dropoff && visit.lineItems && visit.lineItems.length

	const classes = useStyles(isExtendedVisit)

	// const [totalBeforeTip, setTotalBeforeTip] = useState(0)
	// const [surchargeDays, setSurchargeDays] = useState([])
	// const [hasHolidaySurcharge, setHasHolidaySurcharge] = useState(false)

	// console.log('vis...', visit)

	const groomingBathPrice = useMemo(() => {
		if (!visit || visit?.type !== GROOMING) return 0
		return groomingRates?.[visit.pet_ids[0]]?.bath || 0
	}, [groomingRates, visit.pet_ids])


	const isCompleted = useMemo(() => {
		if (!visit) {
			return false
		}

		// completed should be status and the current date should be after the visit end date
		// This ensures that the invoice will look correct until payments run at the end of the day.
		const endOfToday = moment().utc().endOf('day')
		const visitEndDate = moment(visit.endDate).utc().endOf('day')
		return visit.status?.id === STATUS_ID.Completed && endOfToday.isAfter(visitEndDate)
	}, [visit])

	const getCreditsNeeded = () => {
		let number = 0

		if (visit.legacy_compat_session) return 0

		if (visit.type === DAYCARE) {
			visit.pets.forEach((item, i) => {
				number += 1
			})

			return number
		}

		let daycare_days = visit.dates.length - 2
		if (visit.first_day_daycare) daycare_days += 1
		if (visit.last_day_daycare) daycare_days += 1

		return daycare_days * visit.pet_ids.length
	}

	const deletePromo = async () => {
		const visitPromo = visit?.lineItems?.find((i) => i.type === LINE_ITEM_TYPES.VisitPromo)
		const appliedPromo = visit?.applied_promo

		if (visitPromo) {
			dispatch(deleteLineItem(visitPromo))
			return
		}
		if (appliedPromo) {
			const { success } = await deletePromoForFacilityVisit({
				promo_code: appliedPromo.code,
				promo_id: appliedPromo._id,
				visit_id: visit._id,
				household_id: household._id
			})
			return
		}
	}

	const getPrice = (shared) => {
		let number = 0
		let price = 0
		let additionalPrice = 0

		if (visit.type === COMPATIBILITY_SESSION || visit.legacy_compat_session) {
			price = facility.compatibility_session_price || 1000
			const memTypes = [
				MEMBERSHIP_TYPES_ENUM.pet_care_annual,
				MEMBERSHIP_TYPES_ENUM.pet_care_monthly,
				...VIP_MEMBERSHIP_TYPES
			]
			let hasMem = household?.memberships?.find(
				(membership) =>
					matchesFacility(membership, facility?._id) &&
					!membership.deactivated &&
					!moment().isAfter(moment(membership.end_date)) &&
					memTypes.includes(membership.type)
			)
			if (hasMem) price = 0

			const hadTrialMem = validateTrialPeriod(household, visit)
			if (hadTrialMem) price = 0
		} else if (visit.type === DAYCARE) {
			price = facility.services.daycare.prices['1']
			additionalPrice = facility.services.daycare.prices['1']
		} else if (visit.type === BOARDING) {
			price = facility.services.boarding.overnights_price
			if (shared) price = facility.services.boarding.overnights_price_shared
		} else if (visit.type === GROOMING) {
			if (visit.price && visit.price !== visit.quoted_price_min) {
				price = visit.price
			} else {
				price = visit.quoted_price_min || 0
			}
		}

		visit.pets.forEach((item, i) => {
			if (i === 0) {
				number += price
			} else {
				number += additionalPrice
			}
		})

		return number
	}

	const validateTrialPeriod = (household, visit) => {
		const hadTrialMem = household?.memberships?.find(
			(membership) =>
				matchesFacility(membership, facility?._id || visit?.facility_id) &&
				membership.type === MEMBERSHIP_TYPES_ENUM.vip_trial &&
				moment(visit.createdAt).isBetween(moment(membership.start_date), moment(membership.end_date))
		)

		return !!hadTrialMem
	}

	const getGroomingServiceType = () => {
		return GROOMING_SERVICE_LABELS[visit.grooming_service_type] || 'Grooming Service'
	}

	const getDescription = () => {
		let pets = visit.pets
		let numOfPets = pets ? pets.length : 0

		if (visit.legacy_compat_session) return `Legacy Compatibility Session - 1 Dog`

		if (visit.type === DAYCARE) return `Daycare Session (${numOfPets} dog${numOfPets !== 1 ? 's' : ''})`
		if (visit.type === BOARDING) return `Nights (${numOfPets} dog${numOfPets !== 1 ? 's' : ''})`
		if (visit.type === COMPATIBILITY_SESSION)
			return `Compatibility Session (${numOfPets} dog${numOfPets !== 1 ? 's' : ''})`
		if (visit.type === 'grooming') return getGroomingServiceType()
	}

	const getAppliedCredits = (needed) => {
		if (visit.status.id === STATUS_ID.Completed) {
			let applied = 0
			const credits = household?.credits?.daycare.filter((c) => c?.visit_ids?.includes(visit._id))
			if (credits && credits.length) {
				credits.forEach((c) => {
					if (c.visits_used_on)
						c.visits_used_on.forEach((v) => {
							if (v.visit_id === visit._id) applied += v.amount_used
						})
				})
			}

			return applied
		} else {
			let applied = 0
			const credits = household?.credits?.daycare.filter(
				(c) =>
					matchesFacility(c, facility?._id || visit?.facility_id) &&
					!c.expired &&
					!c.used &&
					c.credit_remaining > 0
			)

			if (credits && credits.length) {
				applied = credits.reduce((acc, cred) => acc + cred.credit_remaining, 0)
			}

			if (applied > needed) return needed

			return applied
		}
	}

	// Adds the bath credit for a grooming visit that has an available grooming credit,
	// unless one is already present as a persisted line item. Shared by the branches of
	// the promo/discount chain so they stay consistent (Asana 1213962845250759).
	const addBathCreditIfEligible = (invoiceItems, bathCreditItems) => {
		if (groomingCreditTotal > 0 && visit.type === GROOMING && !bathCreditItems.length) {
			invoiceItems.push({
				price: groomingBathPrice * -1,
				quantity: 1,
				description: 'Bath Credit',
				type: 'bath_credit'
			})
		}
	}
  
	useEffect(() => {
		let cancelled = false

		const resolveVipPromoForInvoice = async () => {
			if (effectiveAppliedPromo) {
				if (!cancelled) {
					setVipResolvedPromo(null)
				}
				return
			}

			const isPreCompletedVisit =
				visit?.status?.id <= STATUS_ID.Completed &&
				![STATUS_ID.Cancelled, STATUS_ID['No Show']].includes(visit?.status?.id)

			if (!isVipOnVisitStartDate || !isPreCompletedVisit || !household?._id) {
				if (!cancelled) {
					setVipResolvedPromo(null)
				}
				return
			}

			const creditsNeeded = getCreditsNeeded()
			const creditsApplied = getAppliedCredits(creditsNeeded)
			const serviceTargets = getVisitPromoServiceTargets(visit, {
				daycareCreditsApplied: creditsApplied > 0
			})

			const visitId = resolveFacilityVisitId(visit)
			const householdId = resolveHouseholdId(household)

			if (!visitId || !householdId) {
				if (!cancelled) {
					setVipResolvedPromo(null)
				}
				return
			}

			let resolvedPromo = null
			// Prefer credits-aware primary target, then visit type — do not let
			// boarding_addons_only overwrite (last-wins) when validating VIPMEMBER.
			const preferredTargets = [
				PROMO_SERVICE_TARGETS.BOARDING_WITH_CREDITS,
				PROMO_SERVICE_TARGETS.DAYCARE_WITH_CREDITS,
				visit.type,
				...serviceTargets
			].filter((target, index, arr) => serviceTargets.includes(target) && arr.indexOf(target) === index)

			for (const service_target of preferredTargets) {
				const { valid, applicable_promo } = await validatePromoForFacilityVisit({
					promo_code: VIP_MEMBER_PROMO_CODE,
					household_id: householdId,
					visit_id: visitId,
					service_target
				})

				if (valid && applicable_promo) {
					resolvedPromo = {
						...applicable_promo,
						service_target_promo_applied_to: service_target
					}
					break
				}
			}

			if (!cancelled) {
				setVipResolvedPromo(resolvedPromo)
			}
		}

		if (visit && household) {
			resolveVipPromoForInvoice()
		} else {
			setVipResolvedPromo(null)
		}

		return () => {
			cancelled = true
		}
	}, [visit, household, isVipOnVisitStartDate, effectiveAppliedPromo])

	useEffect(() => {
		if (visit && facility && household) {
			createInvoice()
		}
	}, [visit, facility, household, addOnBank, promoForInvoice])

	const createInvoice = () => {
		let invoiceItems = []
		let visitItems = []
		let boardingItems = []
		const creditRows = []
		let extended = []
		let early = []
		let holidayRows = []
		let addOnItems
		let options = []
		let bathCreditItems = []

		let pr = getPrice()

		if (![BOARDING, DAYCARE, ADDON_ONLY].includes(visit.type) || visit.legacy_compat_session) {
			console.log('are we in here?')
			if (visit.type === GROOMING) {
				const groomingServicePrice = pr
				if (groomingServicePrice > 0) {
					const bathCreditLineItems = visit?.lineItems?.filter((item) => item.type === 'bath_credit')
					bathCreditItems = bathCreditLineItems.map((item) => ({
						price: item.amount,
						quantity: 1,
						description: 'Bath Credit',
						type: item.type
					}))
				}
			}
			visitItems = [
				{
					price: getPrice(),
					quantity: 1,
					description: getDescription()
				}
			]
		} else {
			if (visit.type !== ADDON_ONLY) {
				let daycare_days = getCreditsNeeded()

				if (daycare_days > 0) {
					visitItems = [
						{
							price: facility.services.daycare.prices['1'],
							quantity: daycare_days,
							description: visit.type === DAYCARE ? 'Daycare Sessions' : 'Daycare Days'
						}
					]
				}

				if (visit.type === DAYCARE) {
					const signaturePlayCount = (visit.boarding_options || []).filter(
						(option) => option.stay_type === 'signature_play'
					).length
					if (signaturePlayCount > 0) {
						const unitPrice = Number(facility.services?.daycare?.signature_play_price)
						const signaturePlayPrice =
							Number.isFinite(unitPrice) && unitPrice >= 0 ? unitPrice : 1500
						const days = visit.dates?.length || 1
						visitItems = [
							...visitItems,
							{
								price: signaturePlayPrice,
								quantity: signaturePlayCount * days,
								description: 'Signature Play'
							}
						]
					}
				}

				let appliedCredits = getAppliedCredits(daycare_days)

				if (appliedCredits) {
					creditRows.push({
						price: facility.services.daycare.prices['1'] * -1,
						quantity: appliedCredits,
						description: 'Credit Bank'
					})
					if (!isCompleted) {
						setShowBankCreditDisclaimer(true)
					}
				}

				if (visit.type === BOARDING) {
					const numOfPets = visit.pet_ids.length
					const nights = visit.dates.length - 1
					const boarding = facility.services.boarding || {}
					const standardRate = boarding.overnights_price
					const comfortPlusRate =
						boarding.comfort_plus_overnights_price || boarding.overnights_price
					const sharedComfortPlusRate = Math.max(
						0,
						comfortPlusRate -
							(Number.isFinite(Number(boarding.comfort_upgrade_sharing_discount))
								? Number(boarding.comfort_upgrade_sharing_discount)
								: 500)
					)
					const boardingOptions = visit.boarding_options || []
					const petIdSet = new Set((visit.pet_ids || []).map((id) => String(id)))
					const comfortPlusCount = boardingOptions.filter(
						(o) => petIdSet.has(String(o.pet_id)) && o.stay_type === 'comfort_plus'
					).length
					const hasComfortPlus = comfortPlusCount > 0
					const allComfortPlus = hasComfortPlus && comfortPlusCount === numOfPets
					const standardCount = Math.max(numOfPets - comfortPlusCount, 0)

					if (visit.sharing_kennels && !hasComfortPlus) {
						const remainder = numOfPets % 2
						const notShared = remainder
						const shared = (numOfPets - remainder) / 2

						if (shared > 0) {
							boardingItems.push({
								price: getPrice(true),
								quantity: nights * shared,
								description: `Overnights (Shared)`
							})
						}

						if (notShared > 0) {
							boardingItems.push({
								price: getPrice(),
								quantity: nights * notShared,
								description: `Overnights`
							})
						}
					} else if (visit.sharing_kennels && allComfortPlus) {
						const oddDogs = numOfPets % 2
						const pairedDogs = numOfPets - oddDogs
						if (pairedDogs > 0) {
							boardingItems.push({
								price: sharedComfortPlusRate,
								quantity: nights * pairedDogs,
								description: 'Signature Stay (Shared)'
							})
						}
						if (oddDogs > 0) {
							boardingItems.push({
								price: comfortPlusRate,
								quantity: nights * oddDogs,
								description: 'Signature Stay (1 Dog)'
							})
						}
					} else {
						if (standardCount > 0) {
							boardingItems.push({
								price: standardRate,
								quantity: nights * standardCount,
								description: hasComfortPlus ? 'Standard Boarding' : 'Overnights'
							})
						}
						if (comfortPlusCount > 0) {
							boardingItems.push({
								price: comfortPlusRate,
								quantity: nights * comfortPlusCount,
								description: 'Signature Stay'
							})
						}
					}
				}
			}
		}

		addOnItems = visit.add_on_services ? visit.add_on_services : []

		let days = []

		if (visit.holiday_id) {
			let h = facility.holidays.find((ho) => ho._id == visit.holiday_id && !!ho.active)
			if (h?.surcharge_dates) {
				h.surcharge_dates.forEach((d, i) => {
					// days.push(d);
					days.push({ date: d, surcharge_fee: h.surcharge_fee })
				})
			}
		} else {
			let holiday

			visit.dates.forEach((d) => {
				let h = facility.holidays.find((ho) => !!ho.active && ho?.surcharge_dates?.includes(d))
				if (h) holiday = h
			})

			if (holiday) {
				holiday.surcharge_dates.forEach((d, i) => {
					// days.push(d);
					days.push({ date: d, surcharge_fee: holiday.surcharge_fee })
				})
			}

			// need to filter out surcharge dates that were added after the visit was created
			days = days.filter((date) => moment(date.created_at).isBefore(visit.createdAt))
		}
		let ourDates = [...visit.dates]
		ourDates.pop()
		let hs = days.filter((d) => ourDates.includes(d.date))
		if (hs.length) {
			let newObj = groupBy(hs, 'surcharge_fee')

			let newArr = Object.keys(newObj)

			holidayRows = newArr.map((price) => {
				return {
					description: 'Holiday Surcharge',
					quantity: newObj[price] ? newObj[price].length : 1,
					price: Number(price)
				}
			})
		}

		if (isExtendedVisit) {
			let found = visit.lineItems.find((l) => l.type === LINE_ITEM_TYPES.ExtendedPickup)

			if (found) {
				extended = [
					{
						...found,
						description: 'Late Pick-up',
						subtext: found.added_by
							? `Added ${found.added_by === 'Automatically' ? '' : 'by '}${found.added_by}`
							: null,
						quantity: 1,
						price: getLatePickupDisplayPrice(),
						type: LINE_ITEM_TYPES.ExtendedPickup
					}
				]
			}
		}

		if (isEarlyDropOff) {
			const found = visit.lineItems.find((l) => l.type === LINE_ITEM_TYPES.EarlyDropoff)

			if (found) {
				early = [
					{
						...found,
						description: 'Early Drop Off',
						subtext: found.added_by
							? `Added ${found.added_by === 'Automatically' ? '' : 'by '}${found.added_by}`
							: null,
						quantity: 1,
						price: getEarlyDropoffDisplayPrice(found.amount),
						type: LINE_ITEM_TYPES.EarlyDropoff
					}
				]
			}
		}

		let cancelFees = visit?.lineItems
			? visit.lineItems
				.filter((i) => i.type === LINE_ITEM_TYPES.PetcareCancellation)
				.map((i) => ({
					price: i.amount,
					description: 'Cancellation Fee',
					quantity: 1
				}))
			: []

		let noShowFees = visit?.lineItems
			? visit.lineItems
				.filter((i) => i.type === LINE_ITEM_TYPES.PetcareNoShow)
				.map((i) => ({
					price: i.amount,
					description: 'No Show Fee',
					quantity: 1
				}))
			: []

		if (noShowFees.length) {
			if (visit.giftcard_amount > 0) {
				noShowFees.push({
					price: visit.giftcard_amount * -1,
					quantity: 1,
					description: 'Gift Card',
					type: LINE_ITEM_TYPES.HouseholdCredit
				})
			}
			if (visit.household_credit_used > 0) {
				noShowFees.push({
					price: visit.household_credit_used * -1,
					quantity: 1,
					description: 'Account Credit',
					type: LINE_ITEM_TYPES.HouseholdCredit
				})
			}
			return setInvoice(noShowFees)
		}

		if (cancelFees.length) {
			if (visit.giftcard_amount > 0) {
				cancelFees.push({
					price: visit.giftcard_amount * -1,
					quantity: 1,
					description: 'Gift Card',
					type: LINE_ITEM_TYPES.HouseholdCredit
				})
			}
			if (visit.household_credit_used > 0) {
				cancelFees.push({
					price: visit.household_credit_used * -1,
					quantity: 1,
					description: 'Account Credit',
					type: LINE_ITEM_TYPES.HouseholdCredit
				})
			}
			return setInvoice(cancelFees)
		}

		if (visit.status.id === STATUS_ID.Cancelled) {

			const bathCreditItem = visit.lineItems.find((i) => i.type === 'bath_credit')
			if (bathCreditItem) {
				return setInvoice([
					{
						price: bathCreditItem.amount * -1,
						description: '- 1x Bath Credit',
						quantity: 1
					}
				])
			}

			return setInvoice([
				{
					price: 0,
					description: 'Cancellation Fee',
					quantity: 1
				}
			])
		}

		if (visit.status.id === STATUS_ID['No Show']) {
			const bathCreditItem = visit.lineItems.find((i) => i.type === 'bath_credit')
			if (bathCreditItem) {
				return setInvoice([
					{
						price: bathCreditItem.amount * -1,
						description: '- 1x Bath Credit',
						quantity: 1
					}
				])
			}
			return setInvoice([
				{
					price: 0,
					description: 'No-Show Fee',
					quantity: 1
				}
			])
		}

		if (
			(facility.add_on_services && addOnItems.length > 0) ||
			(visit.boarding_options && visit.boarding_options.length)
		) {
			// console.log('items....', addOnItems)
			addOnItems = addOnItems
				.filter((addOnItem) => addOnItem.type && addOnItem.category !== MEAL)
				.filter(
					(addOnItem) =>
						visit.status?.id !== STATUS_ID.Completed || addOnItem.completed || visit.type === GROOMING
				)
				.filter((addOnItem) => {
					let allowed = filterAddOnsByTimeBlock(addOnItem, visit)
					return allowed
				})
				.map((addOnItem) => {
					let addOn = facility.add_on_services[addOnItem.type]?.find(
						(addOnService) => addOnService._id === addOnItem.addon_id
					)

					const isPackageIncluded = isSignatureStayPackageAddon(addOnItem, visit, facility)
					const price = isPackageIncluded
						? 0
						: 'price' in addOnItem
							? addOnItem.price
							: addOn
								? addOn.price
								: 0
					let upcharge = 0
					// addon choice logic
					const addon_choice_id = addOnItem.addon_choice_id
					const hasAddonChoice = !!addon_choice_id
					let selectedChoice = null
					if (hasAddonChoice && addOn?.addon_choices?.length) {
						selectedChoice = addOn.addon_choices.find((c) => c.id === addon_choice_id)
						if (selectedChoice && !('price' in addOnItem) && !isPackageIncluded) {
							upcharge += Number(selectedChoice.upcharge)
						}
					}

					return {
						price,
						upcharge,
						quantity: 1,
						type: addOnItem.type,
						addon_id: addOnItem.addon_id,
						addon_choice_id: addOnItem.addon_choice_id,
						description: !hasAddonChoice
							? addOnItem.name
							: `${addOnItem.name} - ${selectedChoice?.name}`,
						completed: addOnItem.completed,
						training_service: addOnItem.training_service || false,
						included_in_comfort_plus: isPackageIncluded
					}
				})

			// grouping enrichments by description which is the addon name before formatting
			addOnItems = addOnItems.length > 1 ? createGroups(addOnItems) : addOnItems

			let boardingOptions =
				Array.isArray(visit.boarding_options) && visit.type === BOARDING
					? visit.boarding_options.filter((o) => {
						let pet_ids = visit.pets.map((p) => p._id)

						return pet_ids.includes(o.pet_id) && o.kennel_type && o.kennel_type !== 'Standard'
					})
					: []

			options = groupBy(boardingOptions, 'kennel_type')

			options = Object.keys(options)
				.map((o) => {
					let kennelOptions = options[o]
					let option = facility.kennel_options.find((ko) => ko.type === o)

					return kennelOptions && kennelOptions.length && option
						? {
							price: visit.sharing_kennels ? option.price : option.price * kennelOptions.length,
							quantity: visit.dates.length - 1,
							description: `${option.public_name}${visit.sharing_kennels
								? ' - Shared'
								: ` (${kennelOptions.length} dog${kennelOptions.length !== 1 ? 's' : ''})`
								}`
						}
						: null
				})
				.filter((o) => o)

			invoiceItems = [
				...visitItems,
				...bathCreditItems,
				...boardingItems,
				...creditRows,
				...options,
				...addOnItems,
				...extended,
				...early,
				...holidayRows,
				...cancelFees,
				...noShowFees
			].filter(Boolean)


			if (visit.type === GROOMING) {
				const totalBeforeTip = invoiceItems.length
					? invoiceItems.filter((item) => item.type !== 'bath_credit').reduce(
						(accu, curr) => {
							return {
								price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
							}
						},
						{ price: 0 }
					)
					: 0

				let desc
				let tip_amt = 0

				if (visit.tip_percent && totalBeforeTip?.price) {
					desc = `Tip (${visit.tip_percent}%)`
					tip_amt = totalBeforeTip.price * (visit.tip_percent / 100)
					tip_amt = Number((tip_amt / 100).toFixed(2)) * 100
				} else if (
					visit.manual_tip_amount ||
					visit.manual_tip_amount === 0 ||
					visit.tip ||
					visit.tip === 0
				) {
					// console.log('this one?')
					desc = 'Tip'
					tip_amt =
						visit.manual_tip_amount || visit.manual_tip_amount === 0
							? visit.manual_tip_amount
							: visit.tip
				} else {
					desc = 'Tip'
					tip_amt = 0
				}

				if (![STATUS_ID.Cancelled, STATUS_ID['No Show']].includes(visit.status.id))
					invoiceItems.push({
						price: tip_amt,
						quantity: 1,
						description: desc,
						tip: true
					})

			}
		} else {
			invoiceItems = [
				...visitItems,
				...bathCreditItems,
				...boardingItems,
				...creditRows,
				...extended,
				...early,
				...holidayRows,
				...cancelFees,
				...noShowFees
			].filter(Boolean)

			if (visit.type === GROOMING) {
				const totalBeforeTip = invoiceItems.length
					? invoiceItems.filter((item) => item.type !== 'bath_credit').reduce(
						(accu, curr) => {
							return {
								price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
							}
						},
						{ price: 0 }
					)
					: 0

				let desc
				let tip_amt = 0

				if (visit.tip_percent && totalBeforeTip?.price) {
					desc = `Tip (${visit.tip_percent}%)`
					tip_amt = totalBeforeTip.price * (visit.tip_percent / 100)
					tip_amt = Number((tip_amt / 100).toFixed(2)) * 100
				} else if (
					visit.manual_tip_amount ||
					visit.manual_tip_amount === 0 ||
					visit.tip ||
					visit.tip === 0
				) {
					// console.log('we in here?')
					desc = 'Tip'
					tip_amt =
						visit.manual_tip_amount || visit.manual_tip_amount === 0
							? visit.manual_tip_amount
							: visit.tip
				} else {
					desc = 'Tip'
					tip_amt = 0
				}

				if (![STATUS_ID.Cancelled, STATUS_ID['No Show']].includes(visit.status.id))
					invoiceItems.push({
						price: tip_amt,
						quantity: 1,
						description: desc,
						tip: true
					})
			}
		}

		const totalWithTips = invoiceItems.length
			? invoiceItems.reduce(
				(accu, curr) => {
					return {
						price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
					}
				},
				{ price: 0 }
			)
			: { price: 0 }

		const totalWithoutTips = invoiceItems.length
			? invoiceItems
				.filter((item) => (item.description ? !item.description.includes('Tip') : true))
				.reduce(
					(accu, curr) => {
						return {
							price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
						}
					},
					{ price: 0 }
				)
			: { price: 0 }

		// HANDLE PROMOS
		// Applied promos will show up 2 ways:
		// 1. As a line item with a negative price and a visit_promo type
		//		- This occurs once the charges have been processed
		//		- can have no subtype, or subtype of 'manual_discount' or 'applied_promo_code'
		// 2. As applied_promo on the visit object
		//		- This occurs before the charges have been processed, which is normally before the visit is completed
		// If there is a visit_promo line item, then we ignore the applied_promo in the invoice (There should only ever be 1)

		const appliedPromo = promoForInvoice

		const visitPromosLineItems =
			visit?.lineItems
				?.filter((i) => i.type === LINE_ITEM_TYPES.VisitPromo)
				.map((i) => ({
					...i,
					price: i.amount,
					description: i.description,
					quantity: 1
				})) || []

		const appliedPromosLineItems = visitPromosLineItems.filter(
			(li) => li.subtype === LINE_ITEM_TYPES.AppliedPromoCode
		)


		const manualDiscountsLineItems = visitPromosLineItems.filter((li) => {
			// old manual discounts won't have a subtype as it was added when the new promotions system was created.
			const { subtype } = li
			if (!subtype) {
				return true
			}
			if (subtype === LINE_ITEM_TYPES.ManualDiscount) {
				return true
			}
			return false
		})

		const {
			appliesToDaycare,
			appliesToBoarding,
			appliesToBoardingAddonsOnly,
			appliesToAddonOnly,
			appliesToGrooming,
			appliesToCredits,
			appliesToTraining,
			appliesToFVRT,
			appliesToVisitAddons
		} = getPromoApplicationFlags(appliedPromo, visit.type, visit)

		const promoAppliesToGeneralVisitAddons =
			appliesToDaycare ||
			appliesToBoarding ||
			appliesToCredits ||
			appliesToAddonOnly ||
			appliesToBoardingAddonsOnly

		let addonsTotal = addOnItems.reduce((acc, i) => acc + (i.price + i.upcharge) * i.quantity, 0)
		let remainingAddonBalance = addonsTotal
		let availableAddonBank = addOnBank || 0
		let remainingAddonBank = addOnBank || 0
		let addonBankAppliedInCents = 0
		let amountApplicableToPromo = 0
		let promoAmountAppliedToAddOnsInCents = 0

		let appliedPromoLineItem = {
			price: 0,
			amount: 0,
			quantity: 1
		}

		let addonBankLineItem = {
			price: 0,
			description: 'Enrichment Bank',
			quantity: 1,
			type: LINE_ITEM_TYPES.AddonBank
		}
		// manual discounts have historically been applied to the total + tip
		if (manualDiscountsLineItems.length) {
			const manualDiscount = manualDiscountsLineItems[0]

			// FOLLOW-UP (Asana 1213962845250759): ourTotal is the pre-bath-credit total,
			// so the discount is capped/applied before the credit. Revisit if it should
			// apply to the post-credit total.
			let ourTotal = totalWithTips.price
			let promo_amt = 0
			if (Math.abs(manualDiscount.amount) > ourTotal) {
				promo_amt = ourTotal
			} else {
				promo_amt = Math.abs(manualDiscount.amount)
			}

			manualDiscount.price = promo_amt * -1
			addBathCreditIfEligible(invoiceItems, bathCreditItems)
			invoiceItems.push(manualDiscount)
		} else if (appliedPromosLineItems.length) {
			appliedPromoLineItem = appliedPromosLineItems[0]
			invoiceItems.push(appliedPromoLineItem)
		} else if (appliedPromo) {
			let daycareTotal = 0
			let boardingTotal = 0
			let extendedPickupTotal = 0
			let earlyDropoffTotal = 0
			let creditsTotal = 0
			let optionsTotal = 0

			// Need to handle $ based promos
			const promoIsCentBased = appliedPromo?.unit === Constants.CENTS
			const promoValue = appliedPromo?.value || 0

			let remainingPromoAmountInCents = promoIsCentBased ? promoValue : -1 // -1 helps us distinguish between $ and % based promos in the calculation logic

			const daycareSessionRows = visitItems.filter((item) => {
				const { description } = item
				return ['Daycare Sessions', 'Daycare Days'].includes(description)
			})

			if (daycareSessionRows.length > 0) {
				const [daycareSessions] = daycareSessionRows
				daycareTotal = daycareSessions.price * daycareSessions.quantity
			}

			const signaturePlayTotal = visitItems
				.filter((item) => item.description === 'Signature Play')
				.reduce((acc, i) => acc + i.price * i.quantity, 0)

			if (creditRows.length > 0) {
				// Credit rows store unit price (negative) + quantity — same as daycare/boarding lines
				creditsTotal = creditRows.reduce((acc, i) => acc + i.price * (i.quantity || 1), 0)
			}

			if (boardingItems.length > 0) {
				boardingTotal = boardingItems.reduce((acc, i) => acc + i.price * i.quantity, 0)
			}

			if (options.length > 0) {
				optionsTotal = options
					.filter((item) => !/Upgrade/.test(item.description))
					.reduce((acc, i) => acc + i.price * i.quantity, 0)
			}

			if (extended.length > 0) {
				extendedPickupTotal = extended.reduce((acc, i) => acc + i.price * i.quantity, 0)
			}

			if (early.length > 0) {
				earlyDropoffTotal = early.reduce((acc, i) => acc + i.price * i.quantity, 0)
			}

			let factorInBoardingNumberOfDays = false

			if (visit.type === DAYCARE) {
				if (appliesToCredits || appliesToDaycare) {
					amountApplicableToPromo =
						daycareTotal + earlyDropoffTotal + extendedPickupTotal + creditsTotal + signaturePlayTotal
				}
			}

			if (visit.type === BOARDING) {
				if (appliesToCredits) {
					amountApplicableToPromo = boardingTotal + daycareTotal + creditsTotal - optionsTotal
					factorInBoardingNumberOfDays = true
				} else if (appliesToBoarding) {
					amountApplicableToPromo = boardingTotal - optionsTotal
					factorInBoardingNumberOfDays = true
				}
			}

			if (visit.type === GROOMING) {
				if (appliesToGrooming) {
					amountApplicableToPromo = totalWithoutTips.price - addonsTotal
				}
			}

			if (visit.type === ADDON_ONLY) {
				if (appliesToAddonOnly) {
					amountApplicableToPromo = totalWithoutTips.price
				}
			}

			const amountToApply = calculatePromoAmountToApply({
				promo: appliedPromo,
				total: amountApplicableToPromo,
				visit,
				factorInBoardingNumberOfDays,
				remainingPromoAmountInCents,
				applyGroomingCredit:
					visit.type === GROOMING && groomingCreditTotal > 0 && pr > 0 && amountApplicableToPromo > 0,
				groomingBathPrice
			})

			promoAmountAppliedToAddOnsInCents += -amountToApply
			if (promoIsCentBased) {
				remainingPromoAmountInCents += amountToApply
			}


			appliedPromoLineItem.price = promoAmountAppliedToAddOnsInCents * -1
			appliedPromoLineItem.description = 'Applied Promo Code'
			appliedPromoLineItem.subtext = appliedPromo.code
			appliedPromoLineItem.type = LINE_ITEM_TYPES.PreLineItemAppliedPromo

			if (addOnItems.length > 0 && visit.type !== ADDON_ONLY && appliesToVisitAddons) {
				for (const addon of addOnItems) {
					if (
						addon.training_service &&
						!(appliesToFVRT || appliesToTraining) &&
						(visit.type === BOARDING || visit.type === DAYCARE)
					) {
						continue
					}

					if (
						!addon.training_service &&
						(appliesToFVRT || appliesToTraining) &&
						!promoAppliesToGeneralVisitAddons &&
						(visit.type === BOARDING || visit.type === DAYCARE)
					) {
						continue
					}

					const addonTotal = (addon.price + addon.upcharge) * addon.quantity
					// $0 / package-included enrichments must not inflate VIPMEMBER %
					if (addonTotal <= 0) {
						continue
					}

					if (promoIsCentBased) {
						if (promoValue > 0) {
							if (remainingPromoAmountInCents > 0) {
								const applicableAmount = getApplicableAmountToApplyToLineItem(
									addonTotal,
									remainingPromoAmountInCents,
									promoAmountAppliedToAddOnsInCents
								)

								remainingAddonBalance -= applicableAmount.individualAmountApplied
								remainingPromoAmountInCents = applicableAmount.remainingBalance
								promoAmountAppliedToAddOnsInCents = applicableAmount.runningAmountApplied
							}
						}
					} else {
						const percentToInt = parseInt(promoValue, 10)
						const subtractedAmount = addonTotal * (percentToInt / 100)
						remainingAddonBalance -= subtractedAmount
						promoAmountAppliedToAddOnsInCents += subtractedAmount
						remainingPromoAmountInCents = -1
					}
				}

				appliedPromoLineItem.price = promoAmountAppliedToAddOnsInCents * -1
			}


			addBathCreditIfEligible(invoiceItems, bathCreditItems)

			invoiceItems.push(appliedPromoLineItem)
		} else if (groomingCreditTotal > 0 && visit.type === GROOMING) {
			addBathCreditIfEligible(invoiceItems, bathCreditItems)

			if (promoAmountAppliedToAddOnsInCents > 0) {
				invoiceItems.push(appliedPromoLineItem)
			}
		}


		if (isCompleted) {
			let num = 0
			visit.add_on_services.forEach((a) => {
				if (a.applied_bank_credit) num += a.applied_bank_credit
			})
			if (num > 0) {
				addonBankLineItem.price = num * -1
				invoiceItems.push(addonBankLineItem)
			}
		} else {
			if (
				availableAddonBank > 0 &&
				remainingAddonBalance > 0 &&
				addOnItems.length > 0 &&
				visit.type !== ADDON_ONLY
			) {
				const applicableBankAmount = getApplicableAmountToApplyToLineItem(
					remainingAddonBalance,
					remainingAddonBank,
					addonBankAppliedInCents
				)
				remainingAddonBalance -= applicableBankAmount.individualAmountApplied
				remainingAddonBank = applicableBankAmount.remainingBalance
				addonBankAppliedInCents = applicableBankAmount.runningAmountApplied

				if (addonBankAppliedInCents > 0) {
					addonBankLineItem.price = addonBankAppliedInCents * -1
					invoiceItems.push(addonBankLineItem)
				}
				if (!isCompleted) {
					setShowBankCreditDisclaimer(true)
				}
			}
		}

		// !! variable name is no longer accurate. tips are included when applying credits.
		// just leaving this in here for now in case it is decided that credits dont apply to tips.
		const finalTotalMinusTips = invoiceItems.length
			? invoiceItems
				// .filter((item) => {
				// 	return !item.description?.includes('Tip')
				// })
				.reduce((acc, i) => {
					const upcharge = i.upcharge || 0
					const total = i.price + upcharge

					return acc + total * i.quantity
				}, 0)
			: 0

		if (!visit.credits_processed === true) {
			if (totalCreditAvailable > 0 && finalTotalMinusTips > 0) {
				if (totalCreditAvailable >= finalTotalMinusTips) {
					invoiceItems.push({
						price: finalTotalMinusTips * -1,
						quantity: 1,
						description: 'Account Credit',
						type: LINE_ITEM_TYPES.HouseholdCredit
					})
				} else {
					invoiceItems.push({
						price: totalCreditAvailable * -1,
						quantity: 1,
						description: 'Account Credit',
						type: LINE_ITEM_TYPES.HouseholdCredit
					})
				}
				if (!isCompleted) {
					setShowBankCreditDisclaimer(true)
				}
			}
		} else {
			if (visit.giftcard_amount > 0) {
				invoiceItems.push({
					price: visit.giftcard_amount * -1,
					quantity: 1,
					description: 'Gift Card',
					type: LINE_ITEM_TYPES.HouseholdCredit
				})
			}
			if (visit.household_credit_used > 0) {
				invoiceItems.push({
					price: visit.household_credit_used * -1,
					quantity: 1,
					description: 'Account Credit',
					type: LINE_ITEM_TYPES.HouseholdCredit
				})
			}
		}

		setInvoice(invoiceItems)
	}

	const createGroups = (addOnItems) => {
		let groupedAddOnItems = groupBy(addOnItems, 'description')

		each(groupedAddOnItems, (value, key) => {
			groupedAddOnItems[key] = groupBy(groupedAddOnItems[key], 'price')
		})

		for (const type in groupedAddOnItems) {
			if (groupedAddOnItems.hasOwnProperty(type) && typeof groupedAddOnItems[type] === 'object') {
				for (const price in groupedAddOnItems[type]) {
					if (
						groupedAddOnItems[type].hasOwnProperty(price) &&
						typeof Array.isArray(groupedAddOnItems[type][price])
					) {
						groupedAddOnItems[type][price] = groupedAddOnItems[type][price].reduce((acc, cur) => {
							return {
								price: acc.price,
								quantity: acc.quantity + cur.quantity,
								description: cur.description,
								upcharge: cur.upcharge
							}
						})
					}
				}
			}
		}

		groupedAddOnItems = Object.keys(groupedAddOnItems)
			.map((groupedAddOnItem) => {
				return Object.keys(groupedAddOnItems[groupedAddOnItem]).map(
					(addOn) => groupedAddOnItems[groupedAddOnItem][addOn]
				)
			})
			.flat(1)

		return groupedAddOnItems
	}

	useEffect(() => {
		if (invoice.length > 0) {
			if (typeof invoice[0] !== 'object' || (!invoice[0].price && invoice[0].price !== 0)) {
				invoice.shift()
			}

			let updatedInvoice = invoice

			const total = updatedInvoice.reduce(
				(accu, curr) => {
					return {
						price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
					}
				},
				{ price: 0 }
			)

			setTotal((total.price / 100).toFixed(2))
			setTotalNumber(total.price)
			if (visit.deposit_paid && visit.type === 'boarding') {
				let due = total.price - visit.deposit_amount
				if (due < 0) due = 0
				setAmtDue(due)
			}
		}
	}, [invoice])

	const disableAddPromoOrDiscountButton = () => {
		if (!visit) {
			return true
		}

		if (visit.status?.id > STATUS_ID.Completed) {
			return true
		}

		const hasPromo = visit.lineItems?.find((i) => i.type === 'visit_promo')
		if (hasPromo) {
			return true
		}

		if (visit.applied_promo) {
			return true
		}

		if (visit.status?.id === STATUS_ID.Completed && visit.credits_processed) {
			return true
		}

		return false
	}

	const showPromo = () => {
		setPromoModal(true)
	}

	const handlePromoDiscountOnComplete = (data) => {
		const { redeemed_promo, manual_discount } = data
		if (redeemed_promo) {
			// promo redemption happens on the api level, so nothing to do here as
			// visit will be updated in state via actions and reducer
			setPromoModal(false)
		}
		if (manual_discount) {
			const existingPromo = visit?.lineItems?.find((i) => i.type === LINE_ITEM_TYPES.VisitPromo)
			if (existingPromo) {
				manual_discount._id = existingPromo._id
				manual_discount.subtype = 'manual_discount'
				dispatch(updateVisitLineItem(existingPromo._id, manual_discount))
			} else {
				dispatch(createItem(manual_discount))
			}
			setPromoModal(false)
		}
	}

	return (
		<Card style={{ marginBottom: '48px' }}>
			<CardContent>
				<InvoiceTable
					invoice={invoice}
					visit={visit}
					facility={facility}
					setEditPromo={setPromoModal}
					setDeletePromo={setDeletePromoShown}
					setIsEditItemPopupShown={setIsEditItemPopupShown}
					setSelectedItem={setSelectedItem}
				/>
				<div>
					<div className={classes.promoWrapper}>
						<Button
							onClick={showPromo}
							style={{
								color: '#6B4AD6',
								fontSize: 14,
								letterSpacing: 1.25,
								opacity: disableAddPromoOrDiscountButton() ? 0.5 : 1
							}}
							disabled={disableAddPromoOrDiscountButton()}
							variant="text">
							Add Promo or Discount
						</Button>
						<div className={classes.totalWrapper}>
							<div className={classes.titleSection}>
								<Typography variant="h6">Total:</Typography>
							</div>
							<div
								className={classes.totalSection}
								style={{
									marginRight: isExtendedVisit || isEarlyDropOff ? 52 : 0
								}}>
								<Typography variant="body" style={{ fontSize: '14px' }}>{`$${total}`}</Typography>
							</div>
						</div>
					</div>
					{visit.deposit_paid && visit.type === 'boarding' ? (
						<div className={classes.totalWrapper}>
							<div className={classes.titleSection}>
								<Typography variant="h6" style={{ fontSize: 14, fontWeight: '600' }}>
									Deposit Paid:
								</Typography>
							</div>
							<div
								className={classes.totalSection}
								style={{
									marginRight: isExtendedVisit || isEarlyDropOff ? 52 : 0
								}}>
								<Typography variant="body" style={{ fontSize: '14px' }}>{`-$${(
									visit.deposit_amount / 100
								).toFixed(2)}`}</Typography>
							</div>
						</div>
					) : null}
					{visit.deposit_paid && visit.type === 'boarding' ? (
						<div className={classes.totalWrapper}>
							<div className={classes.titleSection}>
								<Typography variant="h6">Amount Due:</Typography>
							</div>
							<div
								className={classes.totalSection}
								style={{
									marginRight: isExtendedVisit || isEarlyDropOff ? 52 : 0
								}}>
								<Typography variant="body" style={{ fontSize: '14px' }}>{`$${(
									amtDue / 100
								).toFixed(2)}`}</Typography>
							</div>
						</div>
					) : null}
					{showBankCreditDisclaimer ? (
						<StyledAlert
							type="info"
							text="Please note the invoice total may be adjusted if the client's daycare bank balance drops, their account credit is used or their enrichment bank is used prior to the completion of this visit."
							style={{ marginTop: 8 }}
						/>
					) : null}
				</div>
			</CardContent>
			<PromoAndDiscountEntryDialog
				open={promoModal}
				setIsShown={setPromoModal}
				price={totalNumber}
				visit={visit}
				onComplete={handlePromoDiscountOnComplete}
				onCancel={() => setPromoModal(false)}
			/>
			<DeletePromoConfirmation
				isShown={deletePromoShown}
				setIsShown={setDeletePromoShown}
				confirm={() => {
					deletePromo()
					setDeletePromoShown(false)
					setPromoModal(false)
				}}
			/>
		</Card>
	)
}

export default VisitInvoiceCard
